/**
 * ========================
 * DEPARTMENTS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu for the Departments module (staff departments and their designations, and the ministries
 * people serve in). Sections:
 *  1. Overview: Dashboard
 *  2. Departments: Departments, Designations, Department members
 *  3. Ministries: Ministries, Ministry members, Teams, Equipment, Activities
 *  4. Settings: Ministry roles
 *
 * Visibility is "role OR right": Admin / "Departments Access", or the matching right checked by
 * CCMS.Api.DepartmentManagement (Department, Designation, Ministry, Ministry.Team and so on).
 * createVisibilityCheck would AND roles with rights, so the checks are written out here.
 */

import {
  IconBriefcase,
  IconBuildingChurch,
  IconCalendarEvent,
  IconHierarchy,
  IconLayoutDashboard,
  IconShield,
  IconTool,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuItemVisibility, MenuSection } from '../../layouts/menu/types';

const MODULE_ROLES = ['Admin', 'admin', 'Departments Access'];

const roleOrRight =
  (...rights: string[]): MenuItemVisibility =>
  (ctx) =>
    MODULE_ROLES.some((r) => ctx.userRoles.includes(r)) || rights.some((right) => ctx.userPermissions.includes(right));

class DepartmentsMenuProvider implements DomainMenuProvider {
  domainId = 'departments';

  getMenuConfig() {
    const departments = roleOrRight('Department');
    const designations = roleOrRight('Designation', 'Department');
    const departmentMembers = roleOrRight('Department', 'Member.Department');
    const ministries = roleOrRight('Ministry');
    const anything = roleOrRight('Department', 'Ministry', 'Designation', 'Member.Department');

    const sections: MenuSection[] = [
      {
        id: 'departments-overview',
        label: 'Overview',
        visible: anything,
        items: [{ id: 'departments-dashboard', label: 'Dashboard', path: '/app/departments', icon: <IconLayoutDashboard size={16} />, visible: anything }],
      },
      {
        id: 'departments-management',
        label: 'Departments',
        visible: departments,
        items: [
          { id: 'departments-list', label: 'Departments', path: '/app/departments/list', icon: <IconHierarchy size={16} />, visible: departments },
          { id: 'departments-designations', label: 'Designations', path: '/app/departments/designations', icon: <IconBriefcase size={16} />, visible: designations },
          { id: 'departments-members', label: 'Department members', path: '/app/departments/members', icon: <IconUsers size={16} />, visible: departmentMembers },
        ],
      },
      {
        id: 'departments-ministries',
        label: 'Ministries',
        visible: ministries,
        items: [
          { id: 'ministries-list', label: 'Ministries', path: '/app/departments/ministries', icon: <IconBuildingChurch size={16} />, visible: ministries },
          { id: 'ministries-members', label: 'Ministry members', path: '/app/departments/ministries/members', icon: <IconUsers size={16} />, visible: ministries },
          { id: 'ministries-teams', label: 'Teams', path: '/app/departments/ministries/teams', icon: <IconUsersGroup size={16} />, visible: roleOrRight('Ministry.Team', 'Ministry') },
          { id: 'ministries-equipment', label: 'Equipment', path: '/app/departments/ministries/equipment', icon: <IconTool size={16} />, visible: roleOrRight('Ministry.Equipment', 'Ministry') },
          { id: 'ministries-activities', label: 'Activities', path: '/app/departments/ministries/activities', icon: <IconCalendarEvent size={16} />, visible: roleOrRight('Ministry.Activity', 'Ministry') },
        ],
      },
      {
        id: 'departments-settings',
        label: 'Settings',
        visible: roleOrRight('Ministry.Role', 'Ministry'),
        items: [{ id: 'ministry-roles', label: 'Ministry roles', path: '/app/departments/settings/ministry-roles', icon: <IconShield size={16} />, visible: roleOrRight('Ministry.Role', 'Ministry') }],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Departments and ministries menu',
      },
    };
  }
}

export const departmentsMenuProvider = new DepartmentsMenuProvider();
